import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import {
  AccountsService,
  AppAndAddonActivationStatus,
  ListAppAndAddonActivationStatusFilter,
} from '@vendasta/accounts/legacy';
import { combineLatest, EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { BusinessCenterService } from '../business-center/business-center.service';
import { AGIDTOKEN } from '../local-seo';
import { RepcoreApiService } from '../repcore-api/repcore-api.service';
import { ListingSyncService } from './listing-sync.service';
import { GetMultiListingProfileRequest, ListingProfileApiService } from '@vendasta/listing-products';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'local-seo-listings-management',
  templateUrl: './listings-management.component.html',
  styleUrls: ['./listings-management.component.scss'],
  standalone: false,
})
export class ListingsManagementComponent implements OnInit, OnDestroy {
  @Input() isListingfromEmbedded = false;

  accountGroup$: Observable<AccountGroup>;
  hasCategoryNotFound: boolean;
  redirectLink: string;
  facebookReauthFeatureFlag$: Observable<boolean>;
  showFacebookReconnectBanner$: Observable<boolean>;
  isSmActive$: Observable<boolean>;
  businessAppReconnectLink: string;
  businessAppCustomName: string;
  lspUpgradeHidden$: Observable<boolean>;
  missingGeoLocation$: Observable<boolean>;
  listingProfileRedirectLink: string;

  public listingSyncUnavailable$: Observable<boolean>;
  public listingScoreUnavailable$: Observable<boolean>;

  private readonly subscriptions: Subscription[] = [];

  showTwitterReconnectBanner$: Observable<boolean> = this.accountGroupId$.pipe(
    switchMap((agid) => this.repCoreService.getTwitterCustomAppConnections(agid)),
    map((response) => response.reconnectGroup?.length > 0),
  );

  constructor(
    @Inject(AGIDTOKEN) private accountGroupId$: Observable<string>,
    private repCoreService: RepcoreApiService,
    private accountGroupService: AccountGroupService,
    private businessCenterService: BusinessCenterService,
    private accountsService: AccountsService,
    private listingSyncService: ListingSyncService,
    private listingProfileService: ListingProfileApiService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.lspUpgradeHidden$ = combineLatest([this.listingSyncService.lspActive$, this.listingSyncService.addonID$]).pipe(
      map(([active, addonID]: [boolean, string]) => !active && !addonID),
    );
    this.facebookReauthFeatureFlag$ = this.listingSyncService.featureFlags$.pipe(
      map((flags) => flags['reauth_facebook_account_banner_2023']),
    );

    this.listingSyncUnavailable$ = this.listingSyncService.listingSyncUnavailable$$.asObservable();
    this.listingScoreUnavailable$ = this.listingSyncService.listingScoreUnavailable$;

    this.listingProfileRedirectLink = this.router
      .createUrlTree(['../listing-profile'], { relativeTo: this.route })
      .toString();

    this.missingGeoLocation$ = this.accountGroupId$.pipe(
      switchMap((agid) =>
        this.listingProfileService
          .getMulti(
            new GetMultiListingProfileRequest({
              businessIds: [agid],
              projectionFilter: new ProjectionFilter({
                napData: true,
              }),
            }),
          )
          .pipe(
            catchError((err: HttpErrorResponse) => {
              const message = err.error?.message ? err.error.message : 'Unknown error';
              this.snackBar.open(message, '', { politeness: 'assertive', duration: 5000 });
              return EMPTY;
            }),
            map(
              (res) =>
                !res.listingProfiles[0].listingProfile?.napData?.location?.latitude &&
                !res.listingProfiles[0].listingProfile?.napData?.location?.longitude,
            ),
          ),
      ),
      shareReplay(1),
    );
  }

  ngOnInit(): void {
    this.accountGroupId$.subscribe((agid) => {
      this.subscriptions.push(
        this.accountGroupService
          .get(agid, new ProjectionFilter({ accountGroupExternalIdentifiers: true }))
          .subscribe((data) => {
            const vCat = data.externalIdentifiers.vCategoryIds;
            this.hasCategoryNotFound = vCat.length === 0 || (vCat.length === 1 && vCat.includes('not_found'));
          }),
      );
      this.redirectLink = '/edit/account/' + agid + '/app/listing-profile';
    });

    this.isSmActive$ = this.accountGroupId$.pipe(
      switchMap((agid) => {
        return this.accountsService
          .listAppsAndAddonsActivationStatusesForBusiness(agid, {
            appIds: ['SM'],
            statuses: [AppAndAddonActivationStatus.ACTIVATED, AppAndAddonActivationStatus.CANCELED],
          } as ListAppAndAddonActivationStatusFilter)
          .pipe(
            map((data) => {
              return (
                data?.map(
                  (d) =>
                    d.status === AppAndAddonActivationStatus.ACTIVATED ||
                    d.status === AppAndAddonActivationStatus.CANCELED,
                )?.length > 0
              );
            }),
          );
      }),
    );

    this.showFacebookReconnectBanner$ = combineLatest([
      this.accountGroupId$,
      this.facebookReauthFeatureFlag$,
      this.isSmActive$,
    ]).pipe(
      switchMap(([agid, isFbFeatureFlagEnabled, isSmActive]) => {
        if (isFbFeatureFlagEnabled) {
          if (isSmActive) {
            this.businessCenterService.getVBCEntryUrl(agid, 'settings/connections').subscribe((data) => {
              this.businessAppReconnectLink = data.url;
              this.businessAppCustomName = data.name;
            });
          }

          return this.repCoreService.getFacebookAppConnections(agid).pipe(
            map((response) => {
              return response.reconnectNeeded;
            }),
          );
        }
        return of(false);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
